import React, { useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { CalendarIcon, ArrowLeft, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { SEOHead } from '@/components/SEOHead';
import { useLanguage } from '@/contexts/LanguageContext';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { format, getDay, isAfter, startOfDay } from 'date-fns';
import { fr as frLocale } from 'date-fns/locale';

const CALENDAR_START = new Date(2026, 4, 1); // May 1, 2026

const ReschedulePage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const { toast } = useToast();
  const isFr = language === 'fr';
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [confirmed, setConfirmed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Read user info from query params (passed from email link)
  const firstName = searchParams.get('fn') || '';
  const lastName = searchParams.get('ln') || '';
  const email = searchParams.get('email') || '';
  const trainingTitle = searchParams.get('title') || slug || '';

  const isAvailableDay = (date: Date) => {
    const day = getDay(date);
    return (day === 2 || day === 4) && isAfter(date, startOfDay(new Date()));
  };

  const formattedDate = selectedDate
    ? isFr
      ? format(selectedDate, "EEEE d MMMM yyyy", { locale: frLocale })
      : format(selectedDate, "EEEE, MMMM d, yyyy")
    : null;

  const handleConfirm = async () => {
    if (!selectedDate || !email) {
      // If no email in URL, just confirm visually
      setConfirmed(true);
      return;
    }

    setIsSubmitting(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      await supabase.functions.invoke('send-waitlist-confirmation', {
        body: {
          firstName,
          lastName,
          email,
          trainingTitle,
          trainingSlug: slug,
          selectedDate: dateStr,
        },
      });

      setConfirmed(true);
      toast({
        title: isFr ? 'Date confirmée !' : 'Date confirmed!',
        description: isFr ? 'Un e-mail de confirmation a été envoyé.' : 'A confirmation email has been sent.',
      });
    } catch {
      toast({ variant: 'destructive', title: isFr ? 'Erreur' : 'Error', description: isFr ? 'Impossible d\'envoyer l\'email.' : 'Failed to send email.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={isFr ? 'Choisir une nouvelle date — Gaius' : 'Choose a new date — Gaius'}
        description={isFr ? 'Replanifiez votre session de formation.' : 'Reschedule your training session.'}
      />
      <div className="container max-w-2xl mx-auto py-16 px-4">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8">
          <ArrowLeft className="w-4 h-4" />
          {isFr ? 'Retour à l\'accueil' : 'Back to home'}
        </Link>

        {confirmed ? (
          <Card>
            <CardContent className="py-12 text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-primary" />
              </div>
              <h1 className="text-2xl font-bold text-foreground">
                {isFr ? 'Nouvelle date enregistrée !' : 'New date confirmed!'}
              </h1>
              <p className="text-muted-foreground">
                {isFr
                  ? `Votre session a été replanifiée au ${formattedDate} (12:15 – 14:15). Un e-mail de confirmation vous sera envoyé.`
                  : `Your session has been rescheduled to ${formattedDate} (12:15 – 14:15). A confirmation email will be sent to you.`}
              </p>
              <p className="text-sm text-muted-foreground">
                {isFr ? 'Référence : ' : 'Reference: '}{slug}
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-primary" />
                <CardTitle>{isFr ? 'Choisir une nouvelle date' : 'Choose a new date'}</CardTitle>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {isFr
                  ? 'Sélectionnez un mardi ou jeudi disponible pour votre session de formation.'
                  : 'Select an available Tuesday or Thursday for your training session.'}
              </p>
              {slug && (
                <p className="text-xs text-muted-foreground mt-2">
                  {isFr ? 'Formation : ' : 'Training: '}<span className="font-medium text-foreground">{slug}</span>
                </p>
              )}
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex justify-center">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={(date) => !isAvailableDay(date)}
                  fromDate={CALENDAR_START}
                  locale={isFr ? frLocale : undefined}
                  className="rounded-xl border shadow-sm"
                />
              </div>

              {selectedDate && (
                <div className="p-4 rounded-xl bg-primary/5 border border-primary/20 text-center">
                  <p className="font-semibold text-foreground capitalize">{formattedDate}</p>
                  <p className="text-sm text-muted-foreground">12:15 – 14:15</p>
                </div>
              )}

              <Button onClick={handleConfirm} disabled={!selectedDate || isSubmitting} className="w-full">
                {isSubmitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{isFr ? 'Envoi...' : 'Sending...'}</> : (isFr ? 'Confirmer cette date' : 'Confirm this date')}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ReschedulePage;
